import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import DropdownButton from './DropdownButton';
import Button from './Button';
import ToggleSwitch from './ToggleSwitch';
import CloseIcon from '../icons/CloseIcon';

interface EditRoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: {
    name: string;
    description: string;
    salary: number;
    salaryType: 'point' | 'stamp';
    salaryPeriod: 'daily' | 'weekly' | 'monthly';
    maxCount: number;
    isPointManager: boolean;
    pointAutoPay: boolean;
  }) => void;
  onDelete?: () => void;
  initialData?: {
    name: string;
    description: string;
    salary: number;
    salaryType: 'point' | 'stamp';
    salaryPeriod: 'daily' | 'weekly' | 'monthly';
    maxCount: number;
    isPointManager: boolean;
    pointAutoPay: boolean;
  };
}

const EditRoleDialog: React.FC<EditRoleDialogProps> = ({
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  initialData
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [maxCount, setMaxCount] = useState(2);
  const [salary, setSalary] = useState(230);
  const [salaryType, setSalaryType] = useState<'point' | 'stamp'>('point');
  const [salaryPeriod, setSalaryPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [isPointManager, setIsPointManager] = useState(false);
  const [pointAutoPay, setPointAutoPay] = useState(true);

  // 초기 데이터 설정
  useEffect(() => {
    if (isOpen && initialData) {
      setName(initialData.name || '');
      setDescription(initialData.description || '');
      setMaxCount(initialData.maxCount || 2);
      setSalary(initialData.salary || 230);
      setSalaryType(initialData.salaryType || 'point');
      setSalaryPeriod(initialData.salaryPeriod || 'monthly');
      setIsPointManager(initialData.isPointManager || false);
      setPointAutoPay(initialData.pointAutoPay !== undefined ? initialData.pointAutoPay : true);
    }
  }, [isOpen, initialData]);

  // 다이얼로그가 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    onUpdate({
      name: name.trim(),
      description: description.trim(),
      salary,
      salaryType,
      salaryPeriod,
      maxCount,
      isPointManager,
      pointAutoPay
    });
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDelete = () => {
    if (onDelete) {
      // eslint-disable-next-line no-restricted-globals
      if (window.confirm('정말로 이 역할을 삭제하시겠습니까?')) {
        onDelete();
      }
    }
  };

  const handleDecreaseMaxCount = () => {
    if (maxCount > 1) {
      setMaxCount(maxCount - 1);
    }
  };

  const handleIncreaseMaxCount = () => {
    setMaxCount(maxCount + 1);
  };

  // Portal을 사용하여 body에 직접 렌더링 (레이아웃 transform 영향 회피)
  return createPortal(
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        overflow: 'hidden',
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        // 배경 클릭 시 닫기
        if (e.target === e.currentTarget) {
          handleCancel();
        }
      }}
    >
      <div 
        style={{
          width: '688px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)',
          background: 'white',
          borderRadius: '16px',
          padding: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: '141px',
          overflow: 'auto',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 및 내용 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <div style={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div style={{
                color: '#161616',
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                역할 설정하기
              </div>
              <div style={{
                color: '#989898',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px'
              }}>
                역할 정보를 수정해 주세요
              </div>
            </div>
            <div
              onClick={handleCancel}
              style={{
                padding: '4px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CloseIcon width={24} height={24} />
            </div>
          </div>

          {/* 입력 필드 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '56px'
          }}>
            {/* 역할 이름 및 설명 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                height: '140px'
              }}>
                <InputField
                  value={name}
                  onChange={setName}
                  placeholder="역할을 입력해 주세요"
                  width="100%"
                  height="48px"
                />
                <TextAreaField
                  value={description}
                  onChange={setDescription}
                  placeholder="역할에 대한 설명을 입력해 주세요"
                  width="100%"
                  height="84px"
                  rows={3}
                />
              </div>

              {/* 최대 할당 수, 급여, 급여 종류 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '16px',
                    flexWrap: 'wrap'
                  }}>
                    {/* 최대 할당 가능한 학생 수 */}
                    <div style={{
                      width: '191px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        최대 할당 가능한 학생 수
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <button
                          onClick={handleDecreaseMaxCount}
                          disabled={maxCount <= 1}
                          style={{
                            width: '24px',
                            height: '24px',
                            border: 'none',
                            background: maxCount <= 1 ? '#F0F0F0' : '#FF6F0F',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: maxCount <= 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          -
                        </button>
                        <div style={{
                          width: '40px',
                          textAlign: 'center',
                          color: '#161616',
                          fontSize: '14px',
                          fontFamily: 'Pretendard',
                          fontWeight: '600',
                          lineHeight: '21px'
                        }}>
                          {maxCount}
                        </div>
                        <button
                          onClick={handleIncreaseMaxCount}
                          style={{
                            width: '24px',
                            height: '24px',
                            border: 'none',
                            background: '#FF6F0F',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* 급여 */}
                    <div style={{
                      width: '191px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        급여
                      </div>
                      <InputField
                        type="number"
                        value={salary.toString()}
                        onChange={(v) => setSalary(Number(v))}
                        placeholder="급여를 입력해 주세요"
                        width="100%"
                        height="48px"
                      />
                    </div>

                    {/* 급여 종류 */}
                    <div style={{
                      width: '191px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        급여 종류
                      </div>
                      <DropdownButton
                        options={[
                          { value: 'point', label: '포인트' },
                          { value: 'stamp', label: '도장' }
                        ]}
                        value={salaryType}
                        onChange={(v) => setSalaryType(v as 'point' | 'stamp')}
                        width="100%"
                        height="48px"
                      />
                    </div>

                    {/* 급여 지급 주기 */}
                    <div style={{
                      width: '191px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        급여 지급 주기
                      </div>
                      <DropdownButton
                        options={[
                          { value: 'daily', label: '일급' },
                          { value: 'weekly', label: '주급' },
                          { value: 'monthly', label: '월급' }
                        ]}
                        value={salaryPeriod}
                        onChange={(v) => setSalaryPeriod(v as 'daily' | 'weekly' | 'monthly')}
                        width="100%"
                        height="48px"
                      />
                    </div>
                  </div>
                </div>

                {/* 포인트 관리자 여부, 포인트 자동 지급 여부 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '40px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        포인트 관리자 여부
                      </div>
                      <ToggleSwitch
                        checked={isPointManager}
                        onChange={setIsPointManager}
                        size="medium"
                      />
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        포인트 자동 지급 여부
                      </div>
                      <ToggleSwitch
                        checked={pointAutoPay}
                        onChange={setPointAutoPay}
                        size="medium"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          height: '45px'
        }}>
          {onDelete && (
            <Button
              variant="cancel"
              onClick={handleDelete}
              width="135px"
              height="100%"
            >
              삭제
            </Button>
          )}
          <Button
            variant="cancel"
            onClick={handleCancel}
            width={onDelete ? "135px" : "135px"}
            height="100%"
          >
            취소
          </Button>
          <Button
            variant="submit"
            onClick={handleSubmit}
            width={onDelete ? "418px" : "553px"}
            height="100%"
          >
            저장
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditRoleDialog;
